import { useState } from 'react';
import { Button, Dropdown, Form, Spinner } from 'react-bootstrap';
import { Formik, Field } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

const ActionDropdown = ({ toggleIcon, toggleClass, toggleLabel, children }) => (
  <Dropdown>
    <Dropdown.Toggle
      as={Button}
      size="sm"
      variant="white"
      className="mt-2"
      to={{ hash: '#' }}
    >
      <i className={`mdi ${toggleIcon} me-1 ${toggleClass}`} />
      {toggleLabel}
    </Dropdown.Toggle>
    <Dropdown.Menu
      className="dropdown-menu-right p-3 mt-1"
      style={{ width: '300px' }}
      renderOnMount
      popperConfig={{ strategy: 'fixed' }}
    >
      {children}
    </Dropdown.Menu>
  </Dropdown>
);

ActionDropdown.propTypes = {
  toggleIcon: PropTypes.string,
  toggleClass: PropTypes.string,
  toggleLabel: PropTypes.string,
  children: PropTypes.node,
};

const ChangeRequestApprovalToggle = ({ status, onProcess }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  if (status === 'reject') {
    return (
      <Button size="sm" variant="white" className="mt-2">
        <i className="mdi mdi-close-circle me-1 text-danger" />
        Rejected
      </Button>
    );
  }

  if (status === 'revoke-approval') {
    return (
      <Button size="sm" variant="white" className="mt-2">
        <i className="mdi mdi-cancel me-1 text-danger" />
        Approval Revoked
      </Button>
    );
  }

  if (status === 'cancel') {
    return (
      <Button size="sm" variant="white" className="mt-2">
        <i className="mdi mdi-close-circle me-1 text-danger" />
        Cancelled
      </Button>
    );
  }

  if (status === 'expired') {
    return (
      <Button size="sm" variant="white" className="mt-2">
        <i className="mdi mdi-clock-alert-outline me-1 text-secondary" />
        Expired
      </Button>
    );
  }

  if (status === 'approve') {
    return (
      <ActionDropdown
        toggleIcon="mdi-check-circle"
        toggleClass="text-success"
        toggleLabel="Approved"
      >
        <Formik
          initialValues={{ remarks: '' }}
          onSubmit={(values, { resetForm }) => {
            if (values.remarks.trim().length === 0) {
              toast.error('Please enter a reason to revoke approval');
              return;
            }
            setIsProcessing(true);
            onProcess('revoke-approval', values.remarks, () =>
              setIsProcessing(false)
            );
            resetForm();
          }}
        >
          {(formikprops) => (
            <Form onSubmit={formikprops.handleSubmit}>
              {isProcessing ? (
                <div className="d-flex justify-content-center py-2">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : (
                <>
                  <Form.Group controlId="revokeRemarks" className="mb-3">
                    <Field
                      as="textarea"
                      maxLength="300"
                      name="remarks"
                      rows="2"
                      className="form-control"
                      placeholder="Reason for revoking approval"
                    />
                  </Form.Group>
                  <Button variant="danger" type="submit" size="sm">
                    Revoke Approval
                  </Button>
                </>
              )}
            </Form>
          )}
        </Formik>
      </ActionDropdown>
    );
  }

  // pending (default)
  return (
    <ActionDropdown
      toggleIcon="mdi-check-circle"
      toggleClass="text-warning"
      toggleLabel="Pending"
    >
      <Formik
        initialValues={{ remarks: '' }}
        onSubmit={(values, { resetForm }) => {
          setIsProcessing(true);
          onProcess('approve', values.remarks, () => setIsProcessing(false));
          resetForm();
        }}
      >
        {(formikprops) => (
          <Form onSubmit={formikprops.handleSubmit}>
            {isProcessing ? (
              <div className="d-flex justify-content-center py-2">
                <Spinner animation="border" size="sm" />
              </div>
            ) : (
              <>
                <Form.Group controlId="approvalRemarks" className="mb-3">
                  <Field
                    as="textarea"
                    maxLength="300"
                    name="remarks"
                    rows="2"
                    className="form-control"
                    placeholder="Remarks"
                  />
                </Form.Group>
                <div className="d-flex justify-content-start">
                  <Button
                    variant="primary"
                    type="submit"
                    size="sm"
                    className="me-2"
                  >
                    Approve
                  </Button>
                  <Button
                    variant="white"
                    type="button"
                    size="sm"
                    onClick={() => {
                      if (formikprops.values.remarks.trim().length === 0) {
                        toast.error('Please enter a reason for rejection');
                        return;
                      }
                      setIsProcessing(true);
                      onProcess('reject', formikprops.values.remarks, () =>
                        setIsProcessing(false)
                      );
                    }}
                  >
                    Reject
                  </Button>
                </div>
              </>
            )}
          </Form>
        )}
      </Formik>
    </ActionDropdown>
  );
};

ChangeRequestApprovalToggle.propTypes = {
  status: PropTypes.string,
  onProcess: PropTypes.func,
};

export default ChangeRequestApprovalToggle;
